import React, { useContext } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { isEmpty } from 'lodash';
import NoData from '../NoData';
import { DataContext } from '../../context/DataContext';
import { chartPalette, monoFont } from '../../theme';

/**
 * Lista de barras horizontales para distribuciones (creditos por periodicidad,
 * clientes por municipio, etc.).
 *
 * Reemplaza a los graficos de pastel: con seis o siete categorias, el pastel
 * obligaba a cruzar leyenda y porciones para leer un valor, y las porciones
 * chicas quedaban ilegibles. Aca cada fila dice su nombre y su cifra, y el
 * ancho de la barra da la proporcion de un vistazo.
 *
 * Se ordena de mayor a menor porque el orden en que llegan las categorias no
 * significa nada, y ordenado se lee como un ranking.
 *
 * @param {Array<{label: string, value: number}>} items
 */
const BarList = ({ items = [] }) => {
	const { isDarkMode } = useContext(DataContext);

	if (isEmpty(items)) {
		return <NoData />;
	}

	const ordenados = [...items].sort((a, b) => b.value - a.value);
	const total = ordenados.reduce((suma, item) => suma + (item.value || 0), 0);
	const palette = chartPalette(isDarkMode, Math.max(ordenados.length, 1));

	return (
		<Stack spacing={2}>
			{ordenados.map((item, index) => {
				const pct = total > 0 ? Math.round((item.value / total) * 100) : 0;
				return (
					<Box key={item.label}>
						<Stack
							direction="row"
							justifyContent="space-between"
							alignItems="baseline"
							spacing={1}
							sx={{ marginBottom: '6px' }}
						>
							<Typography
								sx={{
									fontSize: 12.5,
									fontWeight: 500,
									color: 'text.secondary',
									whiteSpace: 'nowrap',
									overflow: 'hidden',
									textOverflow: 'ellipsis'
								}}
								title={item.label}
							>
								{item.label}
							</Typography>
							<Stack direction="row" spacing={1} alignItems="baseline" flexShrink={0}>
								<Typography sx={{ fontSize: 11.5, color: 'text.disabled' }}>{pct}%</Typography>
								<Typography
									sx={{ fontFamily: monoFont, fontSize: 12.5, fontWeight: 600, color: 'text.primary' }}
								>
									{item.value}
								</Typography>
							</Stack>
						</Stack>
						<Box
							sx={{
								height: 8,
								borderRadius: '6px',
								backgroundColor: 'action.hover',
								overflow: 'hidden'
							}}
						>
							<Box
								sx={{
									height: '100%',
									width: `${pct}%`,
									borderRadius: '6px',
									backgroundColor: palette.border[index % palette.border.length],
									transition: 'width .4s ease'
								}}
							/>
						</Box>
					</Box>
				);
			})}
		</Stack>
	);
};

export default BarList;
